import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";

import { FeatureFlagNames, useFeatureFlags } from "@/providers/featureFlags";
import { OrganizationSwitcher } from "@/components/common/OrganizationSwitcher";
import { Logo } from "@/components/ui/Logo";
import {
  ChevronLeft24,
  ConversionPath24,
  Help24,
  IntelligenceTowers24,
  LeaderBoard24,
  ModeOfTravel24,
  MotionSensorActive24,
  NotificationsActive24,
  Pallet24,
} from "@/components/ui/SvgIcon";

import cn from "@/utils/cn";
import { ROUTES } from "@/constants/routes";

import { useSidebarNav } from "./useSidebarNav";

export const SidebarNav = () => {
  const pathname = useLocation().pathname;
  const intl = useIntl();
  const { isCollapsed: contextCollapsed, toggleSidebar } = useSidebarNav();
  const { isEnabled } = useFeatureFlags();
  const isCollapsed = contextCollapsed;

  return (
    <div
      className={cn(
        "relative flex h-full w-full flex-col gap-y-[18px] overflow-hidden bg-chorus-black-500 transition-all duration-300 ease-in-out",
      )}
    >
      <div
        className={cn(
          "mt-6 flex h-16 shrink-0 items-center transition-all duration-300",
          isCollapsed ? "justify-center px-0" : "pl-8 pr-6",
        )}
      >
        {isCollapsed ? (
          <>
            <Link to="/" className="transition-opacity duration-300">
              <svg
                width="26"
                height="48"
                viewBox="0 0 26 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M25.2406 0H21.7383V19.5537H25.2406V0Z"
                  fill="#EF652B"
                />
                <path
                  d="M25.2406 43.3662H21.7383V46.8687H25.2406V43.3662Z"
                  fill="#EF652B"
                />
                <path
                  d="M16.0149 40.8984L12.9258 46.2489L15.9589 48.0002L19.048 42.6495L16.0149 40.8984Z"
                  fill="#EF652B"
                />
                <path
                  d="M12.3008 35.8984L4.63086 40.3267L6.38197 43.3599L14.0519 38.9316L12.3008 35.8984Z"
                  fill="#EF652B"
                />
                <path
                  d="M11.5807 29.709H0.0527344V33.2113H11.5807V29.709Z"
                  fill="#EF652B"
                />
                <path
                  d="M1.75117 16.8877L0 19.9208L12.303 27.0239L14.0541 23.9908L1.75117 16.8877Z"
                  fill="#EF652B"
                />
                <path
                  d="M10.6054 5.65039L7.57227 7.40157L16.0146 22.0239L19.0476 20.2726L10.6054 5.65039Z"
                  fill="#EF652B"
                />
              </svg>
            </Link>
          </>
        ) : (
          <>
            <Link to="/" className="transition-opacity duration-300">
              <Logo />
            </Link>
            <button
              onClick={toggleSidebar}
              className={cn(
                "ml-auto mt-4 hidden size-8 items-center justify-center rounded-md transition-all duration-300 ease-in-out hover:bg-chorus-black-400 xl:flex",
                isCollapsed
                  ? "pointer-events-none translate-x-4 scale-0 opacity-0"
                  : "translate-x-0 scale-100 opacity-100",
              )}
              style={{
                transition:
                  "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              aria-label="Collapse sidebar"
              data-testid="sidebar-collapse-button"
            >
              <ChevronLeft24
                className={cn(
                  "fill-chorus-gray-300 transition-transform duration-300",
                  isCollapsed ? "rotate-180" : "rotate-0",
                )}
              />
            </button>
          </>
        )}
      </div>
      <nav
        className={cn(
          "flex flex-1 flex-col pb-4",
          isCollapsed
            ? "no-scrollbar overflow-hidden"
            : "no-scrollbar overflow-y-auto",
        )}
      >
        <div role="list" className="flex flex-1 flex-col">
          <div
            className={cn(
              "transition-all duration-300",
              isCollapsed ? "px-2" : "px-6",
            )}
          >
            <SidebarNavItemLink
              to={ROUTES.ASSETS}
              icon={<Pallet24 />}
              name={intl.formatMessage(
                defineMessage({
                  defaultMessage: "Assets",
                  description:
                    "Navigation link label in sidebar for accessing customer assets.",
                }),
              )}
              isCurrent={pathname.includes(ROUTES.ASSETS)}
              isCollapsed={isCollapsed}
            />
            <SidebarNavItemLink
              to={ROUTES.TRIPS}
              icon={<ModeOfTravel24 />}
              name={intl.formatMessage(
                defineMessage({
                  defaultMessage: "Trips",
                  description:
                    "Navigation link label in sidebar for accessing trips.",
                }),
              )}
              isCurrent={pathname.includes(ROUTES.TRIPS)}
              isCollapsed={isCollapsed}
            />
            <SidebarNavItemLink
              to={ROUTES.ALERTS}
              icon={<NotificationsActive24 />}
              name={intl.formatMessage(
                defineMessage({
                  defaultMessage: "Alerts",
                  description:
                    "Navigation link label in sidebar for accessing alerts.",
                }),
              )}
              isCurrent={pathname.includes(ROUTES.ALERTS)}
              isCollapsed={isCollapsed}
            />
            <SidebarNavItemLink
              to={ROUTES.REPORTS}
              icon={<LeaderBoard24 />}
              name={intl.formatMessage(
                defineMessage({
                  defaultMessage: "Reports",
                  description:
                    "Navigation link label in sidebar for accessing reports.",
                }),
              )}
              isCurrent={pathname.includes(ROUTES.REPORTS)}
              isCollapsed={isCollapsed}
            />
            {(isEnabled(FeatureFlagNames.IntelligenceTowers) ||
              isEnabled(FeatureFlagNames.TripIntelligenceTowerMarketing)) && (
              <SidebarNavItemLink
                to={ROUTES.TOWERS}
                icon={<IntelligenceTowers24 />}
                name={intl.formatMessage(
                  defineMessage({
                    defaultMessage: "Intelligence Towers",
                    description:
                      "Navigation link label in sidebar for accessing Intelligence Towers.",
                  }),
                )}
                isCurrent={pathname.includes(ROUTES.TOWERS)}
                isCollapsed={isCollapsed}
              />
            )}
          </div>
          <div
            className={cn(
              "mt-auto transition-all duration-300",
              isCollapsed ? "px-2" : "px-6",
            )}
          >
            <SidebarNavItemLink
              to={ROUTES.SYSTEM}
              icon={<MotionSensorActive24 />}
              name={intl.formatMessage(
                defineMessage({
                  defaultMessage: "System",
                  description:
                    "Navigation link label in sidebar for accessing sensors.",
                }),
              )}
              isCurrent={pathname.includes(ROUTES.SYSTEM)}
              isCollapsed={isCollapsed}
            />
            <SidebarNavItemLink
              to={ROUTES.PAIR}
              icon={<ConversionPath24 />}
              name={intl.formatMessage(
                defineMessage({
                  defaultMessage: "Pairing",
                  description:
                    "Navigation link label in sidebar for accessing pairings.",
                }),
              )}
              isCurrent={false}
              isCollapsed={isCollapsed}
            />
            <SidebarNavItemLink
              to={ROUTES.SUPPORT}
              icon={<Help24 />}
              name={intl.formatMessage(
                defineMessage({
                  defaultMessage: "Support",
                  description:
                    "Navigation link label in sidebar for accessing support.",
                }),
              )}
              isCurrent={pathname.includes(ROUTES.SUPPORT)}
              isCollapsed={isCollapsed}
            />
          </div>
          <div className="my-4 border-t border-chorus-black-100"></div>
          <div
            className={cn(
              "transition-all duration-300",
              isCollapsed ? "px-2" : "px-3",
            )}
          >
            <OrganizationSwitcher isCollapsed={isCollapsed} />
          </div>
        </div>
      </nav>
    </div>
  );
};

type Props = {
  to: string;
  name: string;
  icon?: React.ReactElement;
  isCurrent: boolean;
  count?: number;
  isCollapsed: boolean;
};
const SidebarNavItemLink = ({
  to,
  name,
  icon,
  isCurrent,
  count,
  isCollapsed,
}: Props) => {
  if (isCollapsed) {
    return (
      <Link
        to={to}
        className={cn(
          "group relative flex min-h-[64px] flex-col items-center justify-center gap-y-1 rounded-md px-0 py-2 text-md font-medium transition-all duration-300 hover:bg-chorus-black-200",
          isCurrent
            ? "bg-chorus-black-200 text-chorus-gray-200"
            : "text-chorus-gray-300",
        )}
        data-testid={name}
      >
        <div className="relative">
          {icon &&
            React.cloneElement(icon, {
              className: cn(
                "shrink-0 transition-all duration-300",
                isCurrent ? "fill-chorus-gray-400" : "fill-chorus-gray-600",
              ),
            })}
          {Boolean(count) && (
            <span className="absolute -right-1 -top-1 inline-flex size-2 rounded-full bg-chorus-red-600"></span>
          )}
        </div>
        <span className="text-center text-xxs transition-all duration-300">
          {name}
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className={cn(
        "group relative flex h-12 items-center gap-x-[10px] rounded-md py-0 text-md font-medium transition-all duration-300 hover:bg-chorus-black-200",
        "px-3",
        isCurrent
          ? "bg-chorus-black-200 text-chorus-gray-200"
          : "text-chorus-gray-300",
      )}
      data-testid={name}
    >
      {icon &&
        React.cloneElement(icon, {
          className: cn(
            "shrink-0 transition-all duration-300",
            isCurrent ? "fill-chorus-gray-400" : "fill-chorus-gray-600",
          ),
        })}
      <span className="whitespace-nowrap opacity-100 transition-all duration-300">
        {name}
      </span>
      {Boolean(count) && (
        <span className="ml-auto inline-flex h-5 min-w-[30px] items-center justify-center rounded-[20px] bg-chorus-red-600 px-2 text-xxs font-semibold text-chorus-red-100">
          {count}
        </span>
      )}
    </Link>
  );
};
